import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Building2, ArrowUpRight, Home, Briefcase, DollarSign } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LegalFooter from "@/components/legal/LegalFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobsBoard() {
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [seniority, setSeniority] = useState("");
  const [remote, setRemote] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    setData(null);
    const params = { query: q, location, seniority, limit: 30 };
    if (remote !== null) params.remote = remote;
    const { data } = await axios.get(`${API}/recruiter/jobs-board`, { params });
    setData(data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="jobs-board-page">
      <header className="glass-surface sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <Home size={14} /> Career OS
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="font-mono text-[11px] uppercase tracking-widest text-primary mb-3">Jobs Board</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight mb-3">
            {data ? `${data.total} open roles` : "Open roles"}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl leading-relaxed">
            Every role is posted by a verified recruiter using Career OS. No aggregators, no ghost jobs.
          </p>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-8">
          <Input placeholder="Search roles…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="jb-q" />
          <Input placeholder="Location…" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="jb-location" />
          <select
            value={seniority}
            onChange={(e) => setSeniority(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="jb-seniority"
          >
            <option value="">Any level</option>
            <option value="entry">Entry</option>
            <option value="mid">Mid</option>
            <option value="senior">Senior</option>
            <option value="lead">Lead</option>
            <option value="exec">Executive</option>
          </select>
          <select
            value={remote === null ? "" : remote ? "true" : "false"}
            onChange={(e) => setRemote(e.target.value === "" ? null : e.target.value === "true")}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="jb-remote"
          >
            <option value="">Any location</option>
            <option value="true">Remote-friendly</option>
            <option value="false">Onsite / Hybrid</option>
          </select>
          <Button onClick={load} data-testid="jb-search" className="gap-2"><Search size={14} /> Search</Button>
        </div>

        {/* Listings */}
        {!data ? (
          <div className="grid gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="skeleton h-24 rounded-xl" />
            ))}
          </div>
        ) : data.listings.length === 0 ? (
          <div className="border border-border rounded-xl p-14 text-center bg-card">
            <Briefcase size={28} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-display font-bold text-xl mb-2">No matching roles yet</div>
            <div className="text-sm text-muted-foreground max-w-md mx-auto">Broaden your filters or check back tomorrow — new listings posted daily.</div>
          </div>
        ) : (
          <div className="grid gap-3">
            {data.listings.map((l, i) => (
              <motion.article
                key={l.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="bg-card border border-border rounded-xl p-5 card-hover"
                data-testid={`jb-item-${l.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Building2 size={18} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-display font-bold text-lg tracking-tight text-foreground">
                          {l.title}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {l.company}
                          {l.location && <> · <MapPin size={11} className="inline -mt-0.5" /> {l.location}</>}
                          {l.remote && <span className="ml-2 text-[hsl(var(--success))] text-xs font-mono uppercase tracking-widest">Remote</span>}
                        </div>
                      </div>
                      {(l.salary_min || l.salary_max) && (
                        <div className="inline-flex items-center gap-1 text-sm font-mono text-foreground">
                          <DollarSign size={12} />
                          {(l.salary_min || 0).toLocaleString()} – {(l.salary_max || 0).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2 leading-relaxed">
                      {l.description}
                    </p>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <span className="px-1.5 py-0.5 border border-border rounded text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{l.seniority}</span>
                      {(l.tags || []).slice(0, 4).map((t) => (
                        <span key={t} className="px-1.5 py-0.5 bg-secondary text-secondary-foreground rounded text-[10px] font-mono uppercase tracking-widest">{t}</span>
                      ))}
                      {l.apply_url && (
                        <a
                          href={l.apply_url} target="_blank" rel="noreferrer"
                          className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
                          data-testid={`jb-apply-${l.id}`}
                        >
                          Apply <ArrowUpRight size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </main>
      <LegalFooter />
    </div>
  );
}
